'use client';

import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@/layers/presentation/components/ui/Table';
import { useGlobalState } from '@/layers/presentation/context/GlobalStateContext';
import { useTranslation } from '@/layers/presentation/i18n/client';
import { getPatientParamValuesExtended } from '@/layers/presentation/services/patients';
import logger from '@/layers/support/monitoring/logger';
import { formatParamOptionLabel } from '@/layers/support/utils/formatParamOptionLabel';
import { PatientParam, PatientParamValue, PatientParamValues } from '@/types/models';
import { PatientParamValueExtended } from '@/types/patients';
import { Button, Select, SelectItem } from '@nextui-org/react';
import React, { useCallback, useMemo, useState } from 'react';

const LOG_MODULE = 'FE [Presentation / Components / ShowParams]';

// Add this type guard function at the top of the file, outside the component
function isValidParams(
  params: PatientParamValues | null | undefined,
): params is PatientParamValues {
  return params !== null && params !== undefined;
}

interface ShowParamsProps {
  type: 'edit' | 'view';
  params: PatientParamValues | null | undefined;
  deleteParamCallback?: (paramId: string) => void;
  addParamCallback?: (paramId: string, paramValue: PatientParamValue) => void;
  editParamCallback?: (paramId: string, paramValue: PatientParamValue) => void;
}

export default function ShowParams({
  params = {},
  type,
  deleteParamCallback,
  addParamCallback,
  editParamCallback,
}: ShowParamsProps) {
  const { t } = useTranslation();
  const { patientParamsDictionary } = useGlobalState();
  const paramsFull = useMemo(
    () => getPatientParamValuesExtended(patientParamsDictionary, params || {}),
    [patientParamsDictionary, params],
  );

  const [selectedParam, setSelectedParam] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [editingParamId, setEditingParamId] = useState<string | null>(null);

  const usedParamIds = useMemo(() => new Set(Object.keys(params || {})), [params]);

  const paramOptions = useMemo(() => {
    const options = patientParamsDictionary
      .filter(
        (param: PatientParam) =>
          !usedParamIds.has(param.id.toString()) || param.id.toString() === editingParamId,
      )
      .map((param: PatientParam) => ({
        key: param.id.toString(),
        label: param.name,
      }));
    return options;
  }, [patientParamsDictionary, usedParamIds, editingParamId]);

  const disabledParamKeys = useMemo(() => {
    return paramOptions
      .filter(option => {
        const param = patientParamsDictionary.find(p => p.id.toString() === option.key);
        return (
          param &&
          param.parent_param_id != null &&
          param.parent_param_id !== 0 &&
          !usedParamIds.has(param.parent_param_id.toString())
        );
      })
      .map(option => option.key);
  }, [paramOptions, patientParamsDictionary, usedParamIds]);

  const optionOptions = useMemo(() => {
    if (!selectedParam) return [];
    const param = patientParamsDictionary.find(p => p.id.toString() === selectedParam);
    if (!param) return [];

    return param.options.map(option => ({
      key: option.id.toString(),
      // Centralized formatting (e.g. strip the leading zero from padded age
      // options) so the add/edit dropdown matches the read-only display.
      label: formatParamOptionLabel(param.slug, option.option),
    }));
  }, [selectedParam, patientParamsDictionary]);

  const disabledOptionKeys = useMemo(() => {
    if (!selectedParam) return [];
    const param = patientParamsDictionary.find(p => p.id.toString() === selectedParam);
    if (!param) return [];

    const parentOptionIds =
      param.parent_param_id != null && isValidParams(params)
        ? params[param.parent_param_id.toString()] || []
        : [];

    return param.options
      .filter(
        option =>
          option.parent_option_id != null &&
          option.parent_option_id !== 0 &&
          Array.isArray(parentOptionIds) &&
          !parentOptionIds.includes(option.parent_option_id),
      )
      .map(option => option.id.toString());
  }, [selectedParam, patientParamsDictionary, params]);

  const handleParamChange = (value: string) => {
    setSelectedParam(value);
    setSelectedOptions([]);
  };

  const handleOptionsChange = (values: string[]) => {
    setSelectedOptions(values);
  };

  const handleAddParam = () => {
    if (selectedParam && selectedOptions.length > 0 && addParamCallback) {
      addParamCallback(selectedParam, selectedOptions.map(Number));
      handleReset();
    }
  };

  const handleEditParam = () => {
    if (editingParamId && selectedOptions.length > 0 && editParamCallback) {
      editParamCallback(editingParamId, selectedOptions.map(Number));
      setEditingParamId(null);
      setSelectedOptions([]);
    }
  };

  const handleReset = useCallback(() => {
    setEditingParamId(null);
    setSelectedParam(null);
    setSelectedOptions([]);
  }, []);

  const handleDeleteParam = useCallback(
    (paramId: number) => {
      if (deleteParamCallback) {
        deleteParamCallback(paramId.toString());
      }
    },
    [deleteParamCallback],
  );

  const startEditingParam = useCallback(
    (paramId: string) => {
      handleReset();
      setEditingParamId(paramId);
      if (isValidParams(params) && params[paramId]) {
        const paramValue = Array.isArray(params[paramId])
          ? params[paramId].map(String)
          : [params[paramId]];
        setSelectedOptions(paramValue);
      } else {
        setSelectedOptions([]);
      }
      setSelectedParam(paramId);
    },
    [handleReset, params],
  );

  const columns = [
    { key: 'param', label: t('components:showParams.parameter') },
    { key: 'options', label: t('components:showParams.options') },
    ...(type === 'edit' ? [{ key: 'actions', label: t('components:showParams.actions') }] : []),
  ];
  const tableRows = useMemo(() => Object.entries(paramsFull), [paramsFull]);

  const renderCell = useCallback(
    (param: PatientParamValueExtended, columnKey: React.Key): React.ReactNode => {
      const cellValue = param[columnKey as keyof PatientParamValueExtended];

      switch (columnKey) {
        case 'param':
          return param.param.name;

        case 'options':
          const result = param.values
            .filter(v => v.label)
            .map(v => v.label)
            .join(', ');
          return result;

        case 'actions':
          return (
            <div className="flex flex-row gap-2">
              <Button
                size="sm"
                radius="sm"
                variant="bordered"
                onClick={() => startEditingParam(param.param.id.toString())}
              >
                {t('components:showParams.edit')}
              </Button>
              <Button
                size="sm"
                radius="sm"
                variant="bordered"
                color="danger"
                onClick={() => handleDeleteParam(param.param.id)}
              >
                {t('components:showParams.delete')}
              </Button>
            </div>
          );

        default:
          return cellValue != null ? String(cellValue) : null;
      }
    },
    [startEditingParam, handleDeleteParam, t],
  );

  logger.debug(`${LOG_MODULE} - Render`, {
    params,
    patientParamsDictionary,
    paramsFull,
    tableRows,
    selectedParam,
    selectedOptions,
    disabledParamKeys,
    disabledOptionKeys,
  });

  return (
    <>
      {type === 'edit' && (
        <div className="flex flex-row gap-4 mb-8 items-center">
          <Select
            label={t('components:showParams.parameter')}
            onChange={e => handleParamChange(e.target.value)}
            selectedKeys={selectedParam ? [selectedParam] : []}
            disabledKeys={disabledParamKeys}
            isDisabled={!!editingParamId}
          >
            {paramOptions.map(option => (
              <SelectItem key={option.key} value={option.key} textValue={option.label}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
          <Select
            label={t('components:showParams.options')}
            selectionMode="multiple"
            onSelectionChange={keys => handleOptionsChange(Array.from(keys) as string[])}
            selectedKeys={selectedOptions}
            disabledKeys={disabledOptionKeys}
          >
            {optionOptions.map(option => (
              <SelectItem key={option.key} value={option.key} textValue={option.label}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
          {editingParamId ? (
            <Button
              radius="sm"
              color="primary"
              onClick={handleEditParam}
              disabled={selectedOptions.length === 0}
            >
              {t('components:showParams.update')}
            </Button>
          ) : (
            <Button
              radius="sm"
              color="primary"
              onClick={handleAddParam}
              disabled={!selectedParam || selectedOptions.length === 0}
            >
              {t('components:showParams.add')}
            </Button>
          )}
          <Button radius="sm" variant="bordered" onClick={handleReset}>
            {t('components:showParams.reset')}
          </Button>
        </div>
      )}
      <Table aria-label={t('components:showParams.paramsTable')} hideHeader>
        <TableHeader columns={columns}>
          {column => <TableColumn key={column.key}>{column.label}</TableColumn>}
        </TableHeader>
        <TableBody items={tableRows}>
          {item => (
            <TableRow key={item[0]}>
              {columnKey => <TableCell>{renderCell(item[1], columnKey)}</TableCell>}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
}
